// © Microsoft Corporation. All rights reserved.

import { expPersistentDataChangeTrackerKey, forceReloadFlagName } from "./PageLifecycleConstants";
import { headData, localStorage } from "@msnews/core";

import { PageInvalidationReasons } from "./PageInvalidationReasons";
import { getCurrentPerformanceTimeStamp } from "./PrerenderActionHelpers";

/** Type for storing experiences with persistent data updates */
export type ExpWithPersistentDataUpdate = { [expType: string]: number };

/**
 * return true if prerendered page is visible else false
 * @param expType - experience identifier
 */
export function hasExperiencePersistentDataUpdated(expType: string): boolean {
    if (headData.IsPrerender && document.visibilityState === "visible") {
        const storedExperiences = localStorage.getObject<ExpWithPersistentDataUpdate>(expPersistentDataChangeTrackerKey) || {};

        const timeStamp = storedExperiences[expType];
        if (!isNaN(timeStamp)) {
            delete storedExperiences[expType];
            localStorage.setObject(expPersistentDataChangeTrackerKey, storedExperiences);

            if (timeStamp >  performance.timing.navigationStart) {
                return true;
            }
        }
    }

    return false;
}

/**
 * update local storage to track experiences with updated persistent data/settings
 * @param expType - experience identifier
 */
export function trackExpWithPersistentDataUpdate(expType: string): void {
    const storedExperiences = localStorage.getObject<ExpWithPersistentDataUpdate>(expPersistentDataChangeTrackerKey) || {};
    const timeStamp = storedExperiences[expType];

    if (isNaN(timeStamp)) {
        storedExperiences[expType] = getCurrentPerformanceTimeStamp();
        localStorage.setObject(expPersistentDataChangeTrackerKey, storedExperiences);
    }
}

/**
 * Track page invalidation, marks page to be reloaded on reveal.
 * @param expType - experience identifier
 */
export function trackPageInvalidation(invalidationReason: PageInvalidationReasons): void {
    // Write to local storage, current timestamp and invalidation requested reason
    localStorage.setItem(forceReloadFlagName, JSON.stringify({ time: getCurrentPerformanceTimeStamp(), data: invalidationReason }));
}